import { useNavigate } from '@fukuri-admins/hooks/common';
import { useAlertDialog } from '@fukuri-admins/ui/common/AlertDialog';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { usePutPartnerAdminsTopBanner } from '~/hooks';
import { getTopBannerEditLink } from '~/utils/getLinks';
import { TopBannerDropdownMenu } from '..';
import { generateToggleStatusRequest } from '../logics';
import { TopBannerDropdownMenuPresentation } from '../presentations';
import { useDeletePartnerAdminsTopBanner } from './useDeletePartnerAdminsTopBanner';

type TopBannerDropdownMenuProps = React.ComponentProps<typeof TopBannerDropdownMenu>;
type PresentationProps = React.ComponentProps<typeof TopBannerDropdownMenuPresentation>;

type Args = {
  /** トップバナーデータ */
  topBanner: TopBannerDropdownMenuProps['topBanner'];
  /** ドロップダウンメニュー選択時のコールバック */
  onSelected?: TopBannerDropdownMenuProps['onSelected'];
};

type IUseTopBannerDropdownMenu = (args: Args) => {
  actions: {
    handleSelectEdit: PresentationProps['onSelectEdit'];
    handleSelectToggleStatus: PresentationProps['onSelectToggleStatus'];
    handleSelectDelete: PresentationProps['onSelectDelete'];
  };
};

/**
 * トップバナードロップダウンメニューのコンポーネントフック
 *
 * @param args.topBanner - トップバナーデータ
 * @param args.onSelected - ドロップダウンメニュー選択時のコールバック
 * @returns actions.handleSelectEdit - 編集ボタン選択時のハンドラ
 * @returns actions.handleSelectToggleStatus - 表示/非表示切り替えハンドラ
 * @returns actions.handleSelectDelete - 削除ボタン選択時のハンドラ
 */
export const useTopBannerDropdownMenu: IUseTopBannerDropdownMenu = ({ topBanner, onSelected }) => {
  const threeLetterCode = getThreeLetterCode();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { showAlertDialog } = useAlertDialog();

  const { trigger: triggerToggleStatus } = usePutPartnerAdminsTopBanner(
    threeLetterCode,
    topBanner.id,
    {
      onSuccess: () => {
        showToast({
          message: 'ステータスを更新しました。',
          status: 'success',
        });
        onSelected?.();
      },
      onError: error => {
        const errors = error.response?.data?.errors;
        showToast({
          message: errors?.[0] ?? 'ステータスの更新に失敗しました。',
          status: 'danger',
        });
      },
    }
  );

  const { trigger: triggerDelete } = useDeletePartnerAdminsTopBanner(
    threeLetterCode,
    topBanner.id,
    {
      onSuccess: () => {
        showToast({
          message: 'バナーを削除しました。',
          status: 'success',
        });
        onSelected?.();
      },
      onError: error => {
        const errors = error.response?.data?.errors;
        showToast({
          message: errors?.[0] ?? 'バナーの削除に失敗しました。',
          status: 'danger',
        });
      },
    }
  );

  /**
   * 編集ボタン選択時のハンドラ
   * 編集画面へ遷移する
   */
  const handleSelectEdit = () => {
    navigate(getTopBannerEditLink(String(topBanner.id)));
  };

  /**
   * 表示/非表示切り替えハンドラ
   * ステータスを反転してAPIを呼び出し、成功時に一覧を再取得する
   */
  const handleSelectToggleStatus = async () => {
    const body = generateToggleStatusRequest({ topBanner, currentIsActive: topBanner.isActive });
    await triggerToggleStatus(body);
  };

  /**
   * 削除ボタン選択時のハンドラ
   * 確認ダイアログを表示し、確認後にAPIを呼び出して削除する
   */
  const handleSelectDelete = () => {
    showAlertDialog({
      title: `「${topBanner.title}」を削除しますか？`,
      message: `「${topBanner.title}」を削除します。削除すると元に戻すことはできません。`,
      confirmText: '削除',
      confirmButtonVariant: 'destructive',
      onConfirm: async () => {
        await triggerDelete();
      },
    });
  };

  return {
    actions: {
      handleSelectEdit,
      handleSelectToggleStatus,
      handleSelectDelete,
    },
  };
};
